import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Excalidraw, exportToBlob, MIME_TYPES } from '@excalidraw/excalidraw';
import '@excalidraw/excalidraw/index.css';
import './QuestionDiagram.css';
import {
    getDiagramSceneBounds,
    normalizeQuestionDiagram,
} from '../Shared/questionDiagramUtils';

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const DEFAULT_EXCALIDRAW_MIN_HEIGHT = 220;
const DEFAULT_EXCALIDRAW_MAX_HEIGHT = 720;
const EXCALIDRAW_AUTO_WIDTH_PADDING = 96;
const EXCALIDRAW_AUTO_HEIGHT_PADDING = 112;

const buildReadOnlyUiOptions = () => ({
    canvasActions: {
        clearCanvas: false,
        export: false,
        loadScene: false,
        saveToActiveFile: false,
        toggleTheme: false,
        changeViewBackgroundColor: false,
    }
});

const getVisibleSceneElements = (scene) => (
    Array.isArray(scene?.elements)
        ? scene.elements.filter((element) => !element?.isDeleted)
        : []
);

const getNumericDimension = (value, fallback) => (
    typeof value === 'number' && Number.isFinite(value) && value > 0
        ? value
        : fallback
);

const getAdaptiveExcalidrawHeight = ({
    sceneBounds,
    containerWidth,
    minHeight,
    maxHeight,
}) => {
    const resolvedMinHeight = getNumericDimension(minHeight, DEFAULT_EXCALIDRAW_MIN_HEIGHT);
    const resolvedMaxHeight = Math.max(
        resolvedMinHeight,
        getNumericDimension(maxHeight, DEFAULT_EXCALIDRAW_MAX_HEIGHT)
    );

    const sceneWidth = Math.max(1, Number(sceneBounds?.width) || 1);
    const sceneHeight = Math.max(1, Number(sceneBounds?.height) || 1);
    const availableWidth = Math.max(1, Number(containerWidth) || sceneWidth);

    // Wide diagrams can scale down to the available width, but tall diagrams
    // keep enough vertical room so text and labels are not compressed.
    const widthScale = Math.min(1, availableWidth / (sceneWidth + EXCALIDRAW_AUTO_WIDTH_PADDING));
    const contentHeight = Math.ceil((sceneHeight + EXCALIDRAW_AUTO_HEIGHT_PADDING) * widthScale);

    return clamp(contentHeight, resolvedMinHeight, resolvedMaxHeight);
};

const getClipboardPngSupport = () => (
    typeof navigator !== 'undefined' &&
    Boolean(navigator.clipboard?.write) &&
    typeof window !== 'undefined' &&
    typeof window.ClipboardItem !== 'undefined'
);

const parseSvgDimension = (value) => {
    const parsedValue = Number.parseFloat(String(value || '').replace('px', ''));
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getSvgCanvasSize = (svgElement, renderedElement) => {
    const width = parseSvgDimension(svgElement.getAttribute('width'));
    const height = parseSvgDimension(svgElement.getAttribute('height'));
    if (width && height) {
        return { width, height };
    }

    const viewBox = svgElement.getAttribute('viewBox')?.split(/\s+/).map(Number);
    if (viewBox?.length === 4 && Number.isFinite(viewBox[2]) && Number.isFinite(viewBox[3])) {
        return { width: Math.max(1, viewBox[2]), height: Math.max(1, viewBox[3]) };
    }

    const renderedBounds = renderedElement?.getBoundingClientRect?.();
    return {
        width: Math.max(1, Math.round(renderedBounds?.width || 700)),
        height: Math.max(1, Math.round(renderedBounds?.height || 400)),
    };
};

const buildPngBlobFromSvg = (diagramCode, renderedElement) => new Promise((resolve, reject) => {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(diagramCode, 'image/svg+xml');
    const svgElement = parsedDocument.documentElement;

    if (!svgElement || svgElement.nodeName.toLowerCase() !== 'svg' || parsedDocument.querySelector('parsererror')) {
        reject(new Error('Invalid SVG diagram.'));
        return;
    }

    // Clipboard writes need a raster payload, so legacy SVG markup is rendered
    // through an Image and drawn onto a white PNG canvas.
    if (!svgElement.getAttribute('xmlns')) {
        svgElement.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    }

    const { width, height } = getSvgCanvasSize(svgElement, renderedElement);
    const serializedSvg = new XMLSerializer().serializeToString(svgElement);
    const svgBlob = new Blob([serializedSvg], { type: 'image/svg+xml;charset=utf-8' });
    const objectUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
        URL.revokeObjectURL(objectUrl);
        const canvas = document.createElement('canvas');
        canvas.width = Math.ceil(width);
        canvas.height = Math.ceil(height);

        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
            if (blob) {
                resolve(blob);
            } else {
                reject(new Error('Unable to create PNG from SVG diagram.'));
            }
        }, 'image/png');
    };

    image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to render SVG diagram as PNG.'));
    };

    image.src = objectUrl;
});

const buildPngBlobFromExcalidrawScene = (scene, sceneElements) => exportToBlob({
    elements: sceneElements,
    files: scene?.files || {},
    // Export with a white background so pasted diagrams match the review preview.
    appState: {
        ...scene?.appState,
        exportBackground: true,
        viewBackgroundColor: scene?.appState?.viewBackgroundColor || '#ffffff',
    },
    exportPadding: 16,
    mimeType: MIME_TYPES.png,
});

export function QuestionDiagram({
    question,
    diagram,
    className = 'diagram-container',
    style = undefined,
    excalidrawHeight,
    minExcalidrawHeight = DEFAULT_EXCALIDRAW_MIN_HEIGHT,
    maxExcalidrawHeight = DEFAULT_EXCALIDRAW_MAX_HEIGHT,
    fitViewportZoomFactor = 0.9,
    maxAutoZoom = 4,
    allowCopyAsPng = true,
    showLegacyNotice = false,
    legacyNotice = 'Legacy SVG diagram. Display only in the new authoring flow.',
    invalidSceneNotice = 'Diagram data is invalid and could not be displayed.',
}) {
    const normalizedDiagram = useMemo(
        () => normalizeQuestionDiagram(question || diagram || {}),
        [diagram, question]
    );
    const containerRef = useRef(null);
    const svgDiagramRef = useRef(null);
    const [excalidrawApi, setExcalidrawApi] = useState(null);
    const [contextMenu, setContextMenu] = useState(null);
    const [containerWidth, setContainerWidth] = useState(0);
    const sceneElements = useMemo(
        () => getVisibleSceneElements(normalizedDiagram.diagramScene),
        [normalizedDiagram.diagramScene]
    );
    const sceneKey = useMemo(
        () => JSON.stringify(normalizedDiagram.diagramScene || null),
        [normalizedDiagram.diagramScene]
    );
    const displayScene = useMemo(() => (
        normalizedDiagram.diagramScene
            ? {
                ...normalizedDiagram.diagramScene,
                appState: {
                    ...normalizedDiagram.diagramScene.appState,
                    viewModeEnabled: true,
                    zenModeEnabled: true,
                },
            }
            : null
    ), [normalizedDiagram.diagramScene]);

    useEffect(() => {
        if (!excalidrawApi || sceneElements.length === 0) {
            return undefined;
        }

        const animationFrameIds = [];
        const timeoutIds = [];

        // Student/reviewer diagrams should open already fitted and readable. We
        // retry after mount because Excalidraw can report its viewport before
        // fonts, hidden UI chrome, and parent modal layout have fully settled.
        const fitDiagramToViewport = () => {
            excalidrawApi.refresh?.();
            excalidrawApi.scrollToContent(sceneElements, {
                fitToViewport: true,
                viewportZoomFactor: fitViewportZoomFactor,
                minZoom: 0.15,
                maxZoom: maxAutoZoom,
                animate: false,
            });
        };

        const scheduleFit = (delay = 0) => {
            if (delay > 0) {
                const timeoutId = window.setTimeout(() => {
                    const animationFrameId = window.requestAnimationFrame(fitDiagramToViewport);
                    animationFrameIds.push(animationFrameId);
                }, delay);
                timeoutIds.push(timeoutId);
                return;
            }

            const animationFrameId = window.requestAnimationFrame(() => {
                const nestedAnimationFrameId = window.requestAnimationFrame(fitDiagramToViewport);
                animationFrameIds.push(nestedAnimationFrameId);
            });
            animationFrameIds.push(animationFrameId);
        };

        [0, 80, 240, 600].forEach(scheduleFit);

        const resizeObserver = typeof ResizeObserver !== 'undefined' && containerRef.current
            ? new ResizeObserver(() => {
                scheduleFit();
            })
            : null;
        resizeObserver?.observe(containerRef.current);

        return () => {
            animationFrameIds.forEach((animationFrameId) => {
                window.cancelAnimationFrame(animationFrameId);
            });
            timeoutIds.forEach((timeoutId) => {
                window.clearTimeout(timeoutId);
            });
            resizeObserver?.disconnect();
        };
    }, [excalidrawApi, fitViewportZoomFactor, maxAutoZoom, sceneElements, sceneKey]);

    useEffect(() => {
        if (!contextMenu) {
            return undefined;
        }

        // Close the custom diagram menu on the next outside click or Escape key.
        const closeContextMenu = () => setContextMenu(null);
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeContextMenu();
            }
        };

        window.addEventListener('click', closeContextMenu);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('click', closeContextMenu);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [contextMenu]);

    useEffect(() => {
        if (!containerRef.current || typeof ResizeObserver === 'undefined') {
            return undefined;
        }

        const updateContainerWidth = () => {
            const nextWidth = Math.round(containerRef.current?.getBoundingClientRect?.().width || 0);
            setContainerWidth((currentWidth) => (currentWidth === nextWidth ? currentWidth : nextWidth));
        };

        // The adaptive height depends on the actual rendered width, which is not
        // known until after the diagram wrapper mounts inside its modal or page.
        updateContainerWidth();
        const resizeObserver = new ResizeObserver(updateContainerWidth);
        resizeObserver.observe(containerRef.current);

        return () => resizeObserver.disconnect();
    }, [sceneKey]);

    const handleDiagramContextMenu = (event) => {
        event.preventDefault();

        if (!allowCopyAsPng) {
            setContextMenu(null);
            return;
        }

        setContextMenu({
            left: event.clientX,
            top: event.clientY,
            status: getClipboardPngSupport() ? '' : 'Clipboard image copy is not supported in this browser.',
            isCopying: false,
        });
    };

    const handleCopyDiagramAsPng = async (event) => {
        event.stopPropagation();

        if (!getClipboardPngSupport()) {
            setContextMenu((currentMenu) => currentMenu
                ? { ...currentMenu, status: 'Clipboard image copy is not supported in this browser.' }
                : currentMenu);
            return;
        }

        setContextMenu((currentMenu) => currentMenu
            ? { ...currentMenu, status: 'Copying...', isCopying: true }
            : currentMenu);

        try {
            const pngBlob = normalizedDiagram.isSvg
                ? await buildPngBlobFromSvg(normalizedDiagram.diagramCode, svgDiagramRef.current)
                : await buildPngBlobFromExcalidrawScene(displayScene, sceneElements);

            await navigator.clipboard.write([
                new window.ClipboardItem({ 'image/png': pngBlob }),
            ]);

            setContextMenu((currentMenu) => currentMenu
                ? { ...currentMenu, status: 'Copied PNG to clipboard.', isCopying: false }
                : currentMenu);
            window.setTimeout(() => setContextMenu(null), 800);
        } catch (error) {
            console.error('Failed to copy diagram PNG:', error);
            setContextMenu((currentMenu) => currentMenu
                ? { ...currentMenu, status: 'Could not copy this diagram as PNG.', isCopying: false }
                : currentMenu);
        }
    };

    const renderDiagramContextMenu = () => allowCopyAsPng && contextMenu && (
        <div
            className="question-diagram-context-menu"
            style={{ left: contextMenu.left, top: contextMenu.top }}
            onClick={(event) => event.stopPropagation()}
        >
            <button
                type="button"
                className="question-diagram-context-menu__item"
                onClick={handleCopyDiagramAsPng}
                disabled={contextMenu.isCopying || !getClipboardPngSupport()}
            >
                Copy as PNG
            </button>
            {contextMenu.status && (
                <div className="question-diagram-context-menu__status">
                    {contextMenu.status}
                </div>
            )}
        </div>
    );

    useEffect(() => {
        if (!excalidrawApi || sceneElements.length === 0 || typeof document === 'undefined') {
            return undefined;
        }

        let isCancelled = false;

        // Handwritten Excalidraw fonts can change scene bounds after first paint;
        // refit once browser font loading has finished when that API is present.
        document.fonts?.ready?.then(() => {
            if (!isCancelled) {
                window.requestAnimationFrame(() => {
                    excalidrawApi.refresh?.();
                    excalidrawApi.scrollToContent(sceneElements, {
                        fitToViewport: true,
                        viewportZoomFactor: fitViewportZoomFactor,
                        minZoom: 0.15,
                        maxZoom: maxAutoZoom,
                        animate: false,
                    });
                });
            }
        });

        return () => {
            isCancelled = true;
        };
    }, [excalidrawApi, fitViewportZoomFactor, maxAutoZoom, sceneElements, sceneKey]);

    if (!normalizedDiagram.hasDiagram) {
        return null;
    }

    if (normalizedDiagram.isSvg) {
        return (
            <>
                <div className={className} style={style} onContextMenu={handleDiagramContextMenu}>
                    <div
                        ref={svgDiagramRef}
                        dangerouslySetInnerHTML={{ __html: normalizedDiagram.diagramCode }}
                    />
                </div>
                {renderDiagramContextMenu()}
                {showLegacyNotice && (
                    <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text-muted)' }}>
                        {legacyNotice}
                    </div>
                )}
            </>
        );
    }

    if (!normalizedDiagram.diagramScene) {
        return (
            <div className={className} style={style}>
                <div style={{ padding: 12, color: 'var(--text-muted)' }}>
                    {invalidSceneNotice}
                </div>
            </div>
        );
    }

    const sceneBounds = getDiagramSceneBounds(normalizedDiagram.diagramScene);
    const resolvedHeight = excalidrawHeight || getAdaptiveExcalidrawHeight({
        sceneBounds,
        containerWidth,
        minHeight: minExcalidrawHeight,
        maxHeight: maxExcalidrawHeight,
    });
    const containerClassName = `${className} question-diagram-readonly`.trim();

    return (
        <div
            ref={containerRef}
            className={containerClassName}
            onContextMenu={handleDiagramContextMenu}
            style={{
                ...style,
                height: resolvedHeight,
                position: 'relative',
                overflow: 'hidden',
                border: style?.border || '1px solid #e5e7eb',
                borderRadius: style?.borderRadius || 12,
                background: style?.background || '#ffffff',
            }}
        >
            <Excalidraw
                key={sceneKey}
                initialData={displayScene}
                excalidrawAPI={setExcalidrawApi}
                viewModeEnabled={true}
                zenModeEnabled={true}
                gridModeEnabled={false}
                UIOptions={buildReadOnlyUiOptions()}
            />
            {renderDiagramContextMenu()}
        </div>
    );
}

export default QuestionDiagram;
